<template>
  <box-default>
    <div class="col-xs-12">
      <div class="default-box pi-page">
        <h1 class="pi-page__title">Product Intelligence</h1>
        <pi-tabs current="companies"/>
        <pi-filters v-model="filters" :fields="filterFields" @clear="clearFilters"/>

        <section class="pi-section" aria-labelledby="pi-companies-table">
          <div class="pi-section__head">
            <h2 id="pi-companies-table" class="pi-section__title">Empresas</h2>
            <pi-state-legend/>
            <pi-export-menu title="Empresas" screen="Empresas" :columns="tableColumns" :source="exportSource" :filters="exportFilters"
                            :period="exportPeriodValue" :disabled="!request.loaded || !!request.error"/>
          </div>
          <p class="pi-section__hint pi-companies__hint">{{ periodText }} · {{ hintText }}</p>
          <p v-if="summary.known" class="pi-companies__summary">
            <span class="pi-companies__counter">
              <strong>{{ summary.demoText }}</strong>
              <pi-definition-tooltip info-key="companies.demoCount" label="Empresas de demonstração fora dos números"/>
              <span aria-hidden="true">·</span>
              <button type="button" class="pi-companies__see" aria-label="Ver as empresas de demonstração" @click="showInternal('only')">ver</button>
            </span>
            <span v-if="summary.suggestedText" class="pi-companies__counter">
              <strong>{{ summary.suggestedText }}</strong>
              <pi-definition-tooltip info-key="companies.suggestionCount" label="Sugestões para revisar"/>
              <span aria-hidden="true">·</span>
              <button type="button" class="pi-companies__see" aria-label="Ver as sugestões para revisar" @click="showInternal('suggested')">ver</button>
            </span>
          </p>

          <div class="pi-toolbar">
            <div class="pi-toolbar__field">
              <span><label class="pi-toolbar__text" :for="ids.search">Buscar empresa</label><pi-definition-tooltip class="pi-companies__info" info-key="filter.search" label="Buscar empresa"/></span>
              <input :id="ids.search" v-model="searchInput" type="search" class="form-control pi-input" maxlength="60"
                     placeholder="Nome ou id (2+ letras)" @input="onSearchInput" @keydown.enter.prevent="applySearch">
            </div>
            <span v-if="searchHint" class="pi-muted">{{ searchHint }}</span>
            <span v-if="signalText" class="pi-badge pi-badge--bad" role="status">{{ signalText }}</span>
          </div>
          <pi-company-filters v-model="companyFilters" :show-search="false" show-internal/>
          <p v-if="request.loaded" class="pi-muted">{{ countText }}</p>
          <p v-if="quiet.note" class="pi-notice">{{ quiet.note }}</p>

          <pi-data-state :loading="request.loading" :error="request.error" :envelope="request.envelope" :empty="isEmpty"
                         :empty-message="emptyMessage" :show-footer="false" @retry="load(true)">
            <framed-table>
              <thead>
              <tr>
                <template v-for="column in headerColumns">
                  <pi-sort-th v-if="column.sortable" :key="column.key" :column="column" :state="sort" @sort="sortBy"/>
                  <pi-th v-else :key="column.key" :column="column"/>
                </template>
              </tr>
              </thead>
              <tbody>
              <tr v-for="row in rows" :key="row.key">
                <td>
                  <pi-company-name :company-id="row.companyId" :shard-code="row.shardCode" :name="row.name" :show-shard="false"
                                   :demo="row.isDemo" :demo-title="row.demoTitle"/>
                  <span v-if="row.suggestionText" class="pi-badge pi-badge--warn pi-companies__badge" :title="row.suggestionText">
                    Sugestão<span class="pi-sr">: {{ row.suggestionText }}</span>
                  </span>
                  <span class="pi-table__sub">
                    {{ row.shardCode }}
                    <router-link v-if="row.accountRoute" :to="row.accountRoute" class="pi-companies__account">abrir a conta</router-link>
                  </span>
                </td>
                <td>
                  {{ row.sizeBandText }}<span class="pi-table__sub">{{ row.segmentText }}</span>
                  <span v-if="row.journeyText" class="pi-table__sub" title="Jornada do cliente no cadastro (informativo)">{{ row.journeyText }}</span>
                </td>
                <td><span class="pi-badge" :class="'pi-badge--' + row.status.tone">{{ row.status.label }}</span></td>
                <td v-if="showDemoColumn">
                  <template v-if="row.isDemo">{{ row.demoText || "Demonstração" }}</template>
                  <template v-else-if="row.suggestionText">
                    {{ row.suggestionText }}
                    <router-link v-if="reviewLink(row)" :to="reviewLink(row)" class="pi-link pi-companies__review"
                                 :aria-label="'Revisar ' + row.name">Revisar</router-link>
                  </template>
                  <span v-else class="pi-table__sub">—</span>
                </td>
                <td>{{ row.lastActivityText }}</td>
                <td class="is-num">
                  <span v-if="row.activeMissing" class="pi-unmeasured" :title="row.activeTitle">—<span class="pi-sr"> {{ row.activeTitle }}</span></span>
                  <template v-else>{{ row.activeText }}</template>
                  <span v-if="row.usersText" class="pi-table__sub">{{ row.usersText }}</span>
                </td>
                <td class="is-num">
                  <span v-if="row.isDemo" class="pi-unmeasured" :title="row.demoTitle">—<span class="pi-sr"> {{ row.demoTitle }}</span></span>
                  <pi-metric-cell v-else :cell="row.activeSeatsRate"/>
                </td>
                <td class="is-num">{{ row.featuresText }}</td>
                <td><pi-sparkline :points="row.trend" :label="'Ativos de ' + row.name" :width="96" :height="28"/></td>
                <td>
                  <span v-if="row.signal" class="pi-badge" :class="'pi-badge--' + row.signal.tone" :title="row.signal.description">
                    {{ row.signal.label }}
                  </span>
                  <span v-else class="pi-table__sub">—</span>
                </td>
              </tr>
              </tbody>
              <table-pager slot="footer" :total="total" :page="page" :page-size="pageSize"
                           @update:page="changePage" @update:pageSize="changePageSize"/>
            </framed-table>
            <p class="pi-muted pi-companies__legend">
              Queda de atividade = {{ dropText }}
              Empresa sem uso no período nunca é tratada como cancelada.
            </p>
          </pi-data-state>
        </section>

        <pi-page-footer :envelopes="[request.envelope]"/>
      </div>
    </div>
  </box-default>
</template>

<script>
import boxDefault from "../../components/layouts/boxDefault.vue";
import framedTable from "../../components/default/framedTable.vue";
import tablePager from "../../components/default/tablePager.vue";
import piTabs from "../../components/pi/piTabs.vue";
import piFilters from "../../components/pi/piFilters.vue";
import piDataState from "../../components/pi/piDataState.vue";
import piSparkline from "../../components/pi/piSparkline.vue";
import piMetricCell from "../../components/pi/piMetricCell.vue";
import piSortTh from "../../components/pi/piSortTh.vue";
import piTh from "../../components/pi/piTh.vue";
import piPageFooter from "../../components/pi/piPageFooter.vue";
import piCompanyFilters from "../../components/pi/piCompanyFilters.vue";
import piCompanyName from "../../components/pi/piCompanyName.vue";
import piDefinitionTooltip from "../../components/pi/piDefinitionTooltip.vue";
import piStateLegend from "../../components/pi/piStateLegend.vue";
import piExportMenu from "../../components/pi/piExportMenu.vue";
import { listFilterState } from "../../components/default/listFilterState";
import { piGet } from "../../components/pi/piApi.js";
import { screenColumns } from "../../components/pi/piColumns.js";
import { exportPeriod, exportProvide, filterLines } from "../../components/pi/piExport.js";
import { formatInteger, formatPeriod } from "../../components/pi/piFormat.js";
import { companyLink } from "../../components/pi/piNavigation.js";
import { PI_FILTER_DEFAULTS, PI_FILTER_KEY, buildApiQuery, compareEnabled, nextSort, resolvePeriod, toQueryString } from "../../components/pi/piQuery.js";
import { blankRequest, payloadOf, runRequest, shouldLoad } from "../../components/pi/piRequests.js";
import { pageOf, totalOf } from "../../components/pi/piTable.js";
import {
  DEFAULT_COMPANY_SORT, DROP_DESCRIPTION, companiesEmptyText, companiesHint, companyRows, companySortLine, companyTableColumns, demoSummary,
  signalCount,
} from "../../components/pi/piCompaniesModel.js";
import {
  COMPANY_QUERY_KEYS, cleanCompanyFilters, companyFilterParams, companyFiltersFromQuery, hasCompanyFilters, internalFilterLine, internalParam,
  quietSharedPartial, sameCompanyFilters, withCompanyFilters,
} from "../../components/pi/piCompanyFilters.js";
import { PI_COMPANIES } from "../../../../urlConfig";

const FILTER_FIELDS = ["period", "compare", "roleClass", "sizeBand", "segment"];
const SEARCH_DELAY_MS = 350;
const MIN_SEARCH_LENGTH = 2;
const PAGE_SIZE = 25;

export default {
  name: "pi-companies",
  components: {
    boxDefault, framedTable, tablePager, piTabs, piFilters, piDataState, piSparkline, piMetricCell, piSortTh, piTh, piPageFooter, piCompanyFilters,
    piCompanyName, piDefinitionTooltip, piStateLegend, piExportMenu,
  },
  mixins: [listFilterState(PI_FILTER_KEY, PI_FILTER_DEFAULTS)],
  keepQueryKeys: COMPANY_QUERY_KEYS,
  provide: exportProvide,
  data() {
    return {
      ids: { search: "pi-companies-search" },
      filterFields: FILTER_FIELDS,
      request: blankRequest(),
      dropText: DROP_DESCRIPTION,
      sort: Object.assign({}, DEFAULT_COMPANY_SORT),
      page: 1,
      pageSize: PAGE_SIZE,
      searchInput: "",
      searchTerm: "",
      searchTimer: null,
      companyFilters: companyFiltersFromQuery(this.$route && this.$route.query),
    };
  },
  computed: {
    compare() {
      return compareEnabled(this.filters);
    },
    periodText() {
      const period = resolvePeriod(this.filters, new Date());
      return formatPeriod(period.from, period.to);
    },
    internal() {
      return cleanCompanyFilters(this.companyFilters).internal;
    },
    tableColumns() {
      return companyTableColumns(this.internal);
    },
    headerColumns() {
      return screenColumns(this.tableColumns);
    },
    showDemoColumn() {
      return !!this.internal;
    },
    hintText() {
      return companiesHint(this.internal);
    },
    baseParams() {
      return buildApiQuery(this.filters, { fields: FILTER_FIELDS });
    },
    baseKey() {
      return toQueryString(this.baseParams) + "|" + this.searchTerm + "|" + toQueryString(companyFilterParams(this.companyFilters));
    },
    params() {
      const query = buildApiQuery(this.filters, {
        fields: FILTER_FIELDS,
        extra: Object.assign(
          { page: this.page, pageSize: this.pageSize, sort: this.sort.sort, desc: this.sort.desc, search: this.searchTerm },
          companyFilterParams(Object.assign({}, this.companyFilters, { search: "" })),
        ),
      });
      return Object.assign(query, internalParam(this.internal));
    },
    apiKey() {
      return toQueryString(this.params);
    },
    exportSource() {
      return { path: PI_COMPANIES, query: this.params };
    },
    exportFilters() {
      return filterLines({
        filters: this.filters,
        fields: FILTER_FIELDS,
        company: Object.assign({}, this.companyFilters, { search: this.searchTerm }),
        extra: [internalFilterLine(this.internal), companySortLine(this.sort)],
      });
    },
    exportPeriodValue() {
      return exportPeriod(this.filters);
    },
    payload() {
      return payloadOf(this.request.envelope);
    },
    summary() {
      return demoSummary(this.payload);
    },
    quiet() {
      return quietSharedPartial(companyRows(this.payload.rows, this.compare), ["activeSeatsRate"]);
    },
    rows() {
      return this.quiet.rows;
    },
    countText() {
      const total = this.total;
      const text = formatInteger(total) + (total === 1 ? " empresa" : " empresas");
      return hasCompanyFilters(this.companyFilters) ? text + " com os filtros da lista." : text + " no recorte.";
    },
    total() {
      return totalOf(this.payload, this.rows.length);
    },
    isEmpty() {
      return this.request.loaded && !this.rows.length;
    },
    emptyMessage() {
      return companiesEmptyText(this.internal, this.searchTerm, hasCompanyFilters(Object.assign({}, this.companyFilters, { internal: "" })));
    },
    searchHint() {
      const term = this.searchInput.trim();
      return term && term.length < MIN_SEARCH_LENGTH ? "Digite ao menos " + MIN_SEARCH_LENGTH + " letras." : "";
    },
    signalText() {
      const count = signalCount(this.rows);
      if (!count) return "";
      return count + (count === 1 ? " empresa com queda de atividade nesta página" : " empresas com queda de atividade nesta página");
    },
  },
  watch: {
    baseKey() {
      this.page = 1;
    },
    apiKey() {
      this.load(false);
    },
    companyFilters: {
      deep: true,
      handler(value) {
        const query = (this.$route && this.$route.query) || {};
        if (sameCompanyFilters(companyFiltersFromQuery(query), value)) return;
        this.$router.replace({ query: withCompanyFilters(query, value) }).catch(() => {});
      },
    },
    "$route.query"(query) {
      const fromUrl = companyFiltersFromQuery(query);
      if (!sameCompanyFilters(fromUrl, this.companyFilters)) this.companyFilters = fromUrl;
    },
  },
  created() {
    this.load(false);
  },
  beforeDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    load(force) {
      const key = this.apiKey;
      if (!shouldLoad(this.request, key, force)) return;
      runRequest(this.request, () => piGet(PI_COMPANIES, this.params), "carregar as empresas", key);
    },
    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.applySearch, SEARCH_DELAY_MS);
    },
    applySearch() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = null;
      const term = this.searchInput.replace(/\s+/g, " ").trim();
      if (term && term.length < MIN_SEARCH_LENGTH) return;
      this.searchTerm = term.substring(0, 60);
    },
    sortBy(column) {
      this.sort = nextSort(this.sort, column.key, column.startDescending);
      this.page = 1;
    },
    changePage(page) {
      this.page = pageOf({ page: page }, 1);
    },
    changePageSize(size) {
      this.pageSize = size;
      this.page = 1;
    },
    showInternal(mode) {
      this.companyFilters = Object.assign({}, this.companyFilters, { internal: mode });
    },
    reviewLink(row) {
      return companyLink(row.companyId, row.shardCode, this.$route.query);
    },
  },
};
</script>

<style src="../../components/pi/pi.css"></style>

<style scoped>
.pi-companies__account {
  margin-left: 6px;
  font-size: 11px;
  color: #1b59f8;
}

.pi-companies__legend {
  margin-top: 10px;
}

.pi-companies__hint {
  margin: -4px 0 8px;
}

.pi-companies__summary {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 22px;
  margin: 0 0 10px;
  font-size: 12px;
  color: #3a424e;
}

.pi-companies__counter {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.pi-companies__see {
  padding: 0;
  border: 0;
  background: none;
  color: #1b59f8;
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
}

.pi-companies__see:focus {
  outline: 2px solid #1b59f8;
  outline-offset: 2px;
}

.pi-companies__info {
  margin-left: 4px;
  vertical-align: middle;
}

.pi-companies__badge {
  margin-left: 6px;
}

.pi-companies__review {
  margin-left: 6px;
  font-size: 12px;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/productIntelligence/piCompanies.vue